import React from 'react';
import { FilterState } from '../../types';
import { getPriorityColorClass } from '../../utils/taskUtils';

interface FiltersProps {
  filters: FilterState;
  setProjectFilter: React.Dispatch<React.SetStateAction<string[]>>;
  setPriorityFilter: React.Dispatch<React.SetStateAction<string[]>>;
  setTagFilter: React.Dispatch<React.SetStateAction<string[]>>;
  setSectionFilter: React.Dispatch<React.SetStateAction<string[]>>;
  favoriteProjects: string[];
  setFavoriteProjects: React.Dispatch<React.SetStateAction<string[]>>;
  sortedProjects: string[];
  uniqueSections: string[];
  uniqueTags: string[];
  customPriorityValues: string[];
  projectTaskCounts: { [key: string]: number };
  sectionTaskCounts: { [key: string]: number };
  priorityTaskCounts: { [key: string]: number };
  tagTaskCounts: { [key: string]: number };
  onClearAllFilters: () => void;
}

const handleProjectFilterChange = (
  project: string, 
  projectFilter: string[], 
  setProjectFilter: React.Dispatch<React.SetStateAction<string[]>>
) => {
  if (projectFilter.includes(project)) {
    setProjectFilter(prev => prev.filter(p => p !== project));
  } else {
    setProjectFilter(prev => [...prev, project]);
  }
};

const toggleProjectFavorite = (
  project: string, 
  favoriteProjects: string[], 
  setFavoriteProjects: React.Dispatch<React.SetStateAction<string[]>>
) => {
  if (favoriteProjects.includes(project)) {
    setFavoriteProjects(prev => prev.filter(p => p !== project));
  } else {
    setFavoriteProjects(prev => [...prev, project]);
  }
};

const Filters: React.FC<FiltersProps> = ({
  filters,
  setProjectFilter,
  setPriorityFilter,
  setTagFilter,
  setSectionFilter,
  favoriteProjects,
  setFavoriteProjects,
  sortedProjects,
  uniqueSections,
  uniqueTags,
  customPriorityValues,
  projectTaskCounts,
  sectionTaskCounts,
  priorityTaskCounts,
  tagTaskCounts,
  onClearAllFilters,
}) => {
  const handlePriorityFilterChange = (priority: string) => {
    setPriorityFilter(prev => 
      prev.includes(priority) 
        ? prev.filter(p => p !== priority)
        : [...prev, priority]
    );
  };

  const handleTagFilterChange = (tag: string) => {
    setTagFilter(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const handleSectionFilterChange = (section: string) => {
    setSectionFilter(prev => 
      prev.includes(section) 
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  return (
    <div className="filters-sidebar">
      <div className="filters-header">
        <h3>Filters</h3>
        <button onClick={onClearAllFilters} className="clear-filters-btn">
          Clear All
        </button>
      </div>
      
      <div className="filter-section">
        <h4>Projects</h4>
        <div className="multi-select-container">
          {sortedProjects.map(project => {
            const isFavorite = favoriteProjects.includes(project);
            const taskCount = projectTaskCounts[project] || 0;
            return (
              <label key={project} className="checkbox-label project-checkbox">
                <input
                  type="checkbox"
                  checked={filters.projectFilter.includes(project)}
                  onChange={() => handleProjectFilterChange(project, filters.projectFilter, setProjectFilter)}
                />
                <span className="checkbox-text">
                  {project} ({taskCount})
                </span>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    toggleProjectFavorite(project, favoriteProjects, setFavoriteProjects);
                  }}
                  className={`favorite-btn ${isFavorite ? 'favorited' : ''}`}
                  title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                >
                  {isFavorite ? '★' : '☆'}
                </button>
              </label>
            );
          })}
          {sortedProjects.length === 0 && (
            <div className="no-options">No projects available</div>
          )}
        </div>
      </div>
      
      <div className="filter-section">
        <h4>Section</h4>
        <div className="multi-select-container">
          {uniqueSections.map(section => (
            <label key={section} className="checkbox-label">
              <input
                type="checkbox"
                checked={filters.sectionFilter.includes(section)}
                onChange={() => handleSectionFilterChange(section)}
              />
              <span className="checkbox-text">
                {section} ({sectionTaskCounts[section] || 0})
              </span>
            </label>
          ))}
          {uniqueSections.length === 0 && (
            <div className="no-options">No sections available</div>
          )}
        </div>
      </div>
      
      <div className="filter-section">
        <h4>Priority</h4>
        <div className="multi-select-container">
          {customPriorityValues.map((priority: string) => (
            <label key={priority} className="checkbox-label">
              <input
                type="checkbox"
                checked={filters.priorityFilter.includes(priority)}
                onChange={() => handlePriorityFilterChange(priority)}
              />
              <span className={`checkbox-text ${getPriorityColorClass(priority)}`}>
                {priority} ({priorityTaskCounts[priority] || 0})
              </span>
            </label>
          ))}
          {customPriorityValues.length === 0 && (
            <div className="no-options">No priority fields available</div>
          )}
        </div>
      </div>

      <div className="filter-section">
        <h4>Tags</h4>
        <div className="multi-select-container">
          {uniqueTags.map(tag => (
            <label key={tag} className="checkbox-label">
              <input
                type="checkbox"
                checked={filters.tagFilter.includes(tag)}
                onChange={() => handleTagFilterChange(tag)}
              />
              <span className="checkbox-text">
                {tag} ({tagTaskCounts[tag] || 0})
              </span>
            </label>
          ))}
          {uniqueTags.length === 0 && (
            <div className="no-options">No tags available</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Filters; 